"use client";

import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { RichTextValue } from "../RichTextField";
import { Button } from "@/components/shadcn/button";
import {
  Sparkles,
  ArrowUp,
  Loader2,
  ArrowLeft,
  Search,
  TriangleAlert,
} from "lucide-react";
import { Separator } from "@/components/shadcn/separator";
import { Textarea } from "@/components/shadcn/textarea";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
import { injectMentionsIntoDoc } from "./chipUtils";
import { HeuristicScoreBadge, ScoresEnum } from "./HeuristicScoreBadge";
import {
  DEFAULT_HEURISTIC_ORDER,
  HeuristicFeedback,
  buildRadarData,
} from "./HeuristicGauge";
import {
  HeuristicEvaluationResponse,
  HeuristicScoreResponse,
  InputSpec,
} from "@/generated";
import {
  runHeuristicImprovementJob,
  isAllModelsDisabledError,
  PromptJobTimeoutError,
} from "@/services/promptHeuristicJobs";
import { useSpotUser } from "@/utils/spotUser";
import { SpotToast } from "@/components/SpotToast";
import * as Sentry from "@sentry/nextjs";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import { AnimatePresence, motion } from "framer-motion";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { cn } from "@/lib/utils";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import {
  allChipValues,
  allChipSpecs,
  type AdvancedChipOption,
} from "./chipOptionsConfig";

function parseImprovementError(error: unknown): string {
  const axiosErr = error as {
    response?: { status?: number };
  };
  if (isAllModelsDisabledError(error)) {
    return "No AI models are available for this workspace. Contact your administrator.";
  }
  if (
    error instanceof PromptJobTimeoutError ||
    axiosErr.response?.status === 504
  ) {
    return "Improvement timed out. Please try again.";
  }
  return "Error improving prompt. Please try again.";
}

function getChartColors(score: number | null) {
  if (score === null) {
    return {
      dot: "bg-muted-foreground/50",
      text: "text-muted-foreground",
      bg: "bg-transparent border-border",
    };
  }
  if (score >= ScoresEnum.strong) {
    return {
      stroke: "var(--color-green-800)",
      fill: "var(--color-green-400)",
    };
  }
  if (score >= ScoresEnum.fair) {
    return {
      stroke: "var(--color-orange-400)",
      fill: "var(--color-amber-400)",
    };
  }
  return {
    stroke: "var(--color-destructive)",
    fill: "var(--color-destructive)",
  };
}
interface HeuristicsSectionProps {
  text: string;
  onAppendRefinement: (value: RichTextValue) => void;
  chipOptions?: AdvancedChipOption[];
  // Shared heuristic state from parent hook
  heuristicResults: HeuristicEvaluationResponse | null;
  isLoadingHeuristics: boolean;
  isOutdated: boolean;
  canAnalyse: boolean;
  triggerEvaluation?: () => void;
  scheduleReanalysis?: () => void;
  improverContext?: string;
  isError?: boolean;
  errorMessage?: string | null;
  retryEvaluation?: () => void;
  // Overrides the `inputs` sent to the improver. When omitted, specs are
  // derived from `chipOptions` (TipTap callers). Markdown callers, which have
  // no chip options, pass their variable specs directly.
  improverInputs?: Array<InputSpec> | Array<string>;
  // Notation the improver should emit for variable placeholders. Defaults to
  // the TipTap `((name))` form; markdown callers pass "var_directive" for
  // `:var[name]`.
  variableSyntax?: "double_paren" | "var_directive";
}

export function HeuristicsSection({
  text,
  onAppendRefinement,
  chipOptions,
  heuristicResults,
  isLoadingHeuristics,
  isOutdated,
  canAnalyse,
  triggerEvaluation,
  scheduleReanalysis,
  improverContext,
  isError,
  errorMessage,
  retryEvaluation,
  improverInputs,
  variableSyntax,
}: HeuristicsSectionProps) {
  const { spotGroup } = useSpotUser();
  const flatChipValues = useMemo(
    () => (chipOptions ? allChipValues(chipOptions) : []),
    [chipOptions]
  );
  const flatChipSpecs = useMemo(
    () => (chipOptions ? allChipSpecs(chipOptions) : []),
    [chipOptions]
  );

  const effectiveInputs = useMemo(
    () => improverInputs ?? (flatChipSpecs.length ? flatChipSpecs : undefined),
    [improverInputs, flatChipSpecs]
  );

  // Hidden editor for converting markdown text → TipTap JSON definition with mentions
  const converterEditor = useEditor({
    extensions: [StarterKit, Markdown],
    content: "",
    immediatelyRender: false,
  });

  const [instructionsInput, setInstructionsInput] = useState("");
  const [isImproving, setIsImproving] = useState(false);
  const [improvementError, setImprovementError] = useState<string | null>(null);
  const [selectedHeuristic, setSelectedHeuristic] = useState<string | null>(
    null
  );
  const [transitionDirection, setTransitionDirection] = useState<1 | -1>(1);

  const editorTextRef = useRef(text);
  useEffect(() => {
    editorTextRef.current = text;
  }, [text]);

  // Abort in-flight improvement job polling on unmount or when superseded
  const improveAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    return () => {
      improveAbortRef.current?.abort();
    };
  }, []);

  const isAnalysing = isLoadingHeuristics && !heuristicResults;

  const weightedScore = heuristicResults?.weighted_score ?? null;
  const score = weightedScore !== null ? weightedScore * 10 : null;
  const isStrongScore = typeof score === "number" && score >= ScoresEnum.strong;
  const isTextTooShortToAnalyse = isOutdated && !canAnalyse;
  const isScoreTooLowToRefine =
    !isOutdated && weightedScore !== null && weightedScore < 2;

  const refineButtonTooltip = useMemo(() => {
    if (isTextTooShortToAnalyse) {
      return "Write at least 200 characters to run analysis.";
    }
    if (isScoreTooLowToRefine) {
      return "Score is too low to refine. Write a more complete prompt first.";
    }
    if (isStrongScore && !isImproving && !isLoadingHeuristics && !isOutdated) {
      return "This prompt is already optimized.";
    }
    return "";
  }, [
    isTextTooShortToAnalyse,
    isScoreTooLowToRefine,
    isStrongScore,
    isImproving,
    isLoadingHeuristics,
    isOutdated,
  ]);

  const isRefineButtonDisabled =
    isImproving ||
    isLoadingHeuristics ||
    (isStrongScore && !isOutdated) ||
    isTextTooShortToAnalyse ||
    isScoreTooLowToRefine;

  // Reset selected heuristic when results update
  useEffect(() => {
    if (heuristicResults) {
      setSelectedHeuristic(null);
    }
  }, [heuristicResults]);

  const chartColors = getChartColors(score);

  // Radar chart data
  const evaluatedTypes =
    heuristicResults?.evaluated_types ?? DEFAULT_HEURISTIC_ORDER;
  const heuristicOrder = DEFAULT_HEURISTIC_ORDER.filter((h) =>
    evaluatedTypes.includes(h)
  );
  const radarData = buildRadarData(heuristicResults, evaluatedTypes);

  const appendRefinement = useCallback(
    (newText: string) => {
      let definition: Record<string, any>;

      if (converterEditor) {
        // Parse markdown into TipTap JSON, then inject mention nodes for ((chip)) patterns
        converterEditor.commands.setContent(newText);
        const parsed = converterEditor.getJSON();
        definition = injectMentionsIntoDoc(parsed, chipOptions);
      } else {
        definition = {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [{ type: "text", text: newText }],
            },
          ],
        };
      }

      const newValue: RichTextValue = { text: newText, definition };
      editorTextRef.current = newText;
      onAppendRefinement(newValue);
      scheduleReanalysis?.();
    },
    [converterEditor, onAppendRefinement, scheduleReanalysis, chipOptions]
  );

  const fullImproverContext = useMemo(() => {
    if (!improverContext) return undefined;
    if (!flatChipValues.length) return improverContext;
    const varList = flatChipValues.map((v) => `((${v}))`).join(", ");
    return (
      `${improverContext}\n` +
      `Available input variables: ${varList}\n` +
      `Keep every input variable the prompt already uses. Add another one only ` +
      `when the task needs it.\n` +
      `Note: Variables can be value placeholders ((Name)) replaced at runtime, ` +
      `or reference placeholders ((@Name)) kept as references. Preserve each variable's type.`
    );
  }, [improverContext, flatChipValues]);

  const handleRefineFromAnalysis = useCallback(() => {
    if (!heuristicResults || isImproving) return;
    const promptText = editorTextRef.current;
    if (!promptText) return;

    setIsImproving(true);
    setImprovementError(null);

    improveAbortRef.current?.abort();
    const controller = new AbortController();
    improveAbortRef.current = controller;

    runHeuristicImprovementJob({
      path: { group_id: spotGroup.id },
      body: {
        prompt: promptText,
        heuristic_results: heuristicResults,
        inputs: effectiveInputs,
        context: fullImproverContext,
        variable_syntax: variableSyntax,
      },
      signal: controller.signal,
    })
      .then((response) => {
        if (controller.signal.aborted) return;
        if (response.data.prompt) {
          appendRefinement(response.data.prompt);
        }
        setIsImproving(false);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        const msg = parseImprovementError(error);
        SpotToast.error(msg);
        setImprovementError(msg);
        Sentry.captureException(error, {
          extra: { initial_prompt: editorTextRef.current },
        });
        setIsImproving(false);
      });
  }, [
    heuristicResults,
    isImproving,
    spotGroup.id,
    effectiveInputs,
    appendRefinement,
    fullImproverContext,
    variableSyntax,
  ]);

  const handleRefineWithInstructions = useCallback(() => {
    if (!instructionsInput || isImproving) return;
    const promptText = editorTextRef.current;

    setIsImproving(true);
    setImprovementError(null);

    improveAbortRef.current?.abort();
    const controller = new AbortController();
    improveAbortRef.current = controller;

    runHeuristicImprovementJob({
      path: { group_id: spotGroup.id },
      body: {
        prompt: promptText,
        feedback: instructionsInput,
        heuristic_results: heuristicResults ?? undefined,
        inputs: effectiveInputs,
        context: fullImproverContext,
        variable_syntax: variableSyntax,
      },
      signal: controller.signal,
    })
      .then((response) => {
        if (controller.signal.aborted) return;
        if (response.data.prompt) {
          appendRefinement(response.data.prompt);
        }
        setIsImproving(false);
        setInstructionsInput("");
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        const msg = parseImprovementError(error);
        SpotToast.error(msg);
        setImprovementError(msg);
        Sentry.captureException(error, {
          extra: {
            initial_prompt: editorTextRef.current,
            user_request: instructionsInput,
          },
        });
        setIsImproving(false);
      });
  }, [
    instructionsInput,
    isImproving,
    spotGroup.id,
    heuristicResults,
    effectiveInputs,
    appendRefinement,
    fullImproverContext,
    variableSyntax,
  ]);

  const getSelectedScore = (): HeuristicScoreResponse | null => {
    if (!selectedHeuristic || !heuristicResults) return null;
    const scoreData =
      heuristicResults[selectedHeuristic as keyof HeuristicEvaluationResponse];
    if (scoreData && typeof scoreData === "object" && "score" in scoreData) {
      return scoreData as HeuristicScoreResponse;
    }
    return null;
  };

  const selectedScore = getSelectedScore();

  const handleLabelClick = useCallback(
    (data: { index?: number }) => {
      if (data.index !== undefined && data.index < heuristicOrder.length) {
        setTransitionDirection(1);
        setSelectedHeuristic(heuristicOrder[data.index]);
      }
    },
    [heuristicOrder]
  );

  return (
    <>
      {/* Heuristics panel */}
      <div className="p-6 pb-2 flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* Header row */}
        <div className="flex items-center justify-between mb-6 flex-shrink-0">
          {selectedHeuristic && selectedScore ? (
            <Button
              className="p-0 border-none h-auto hover:bg-transparent hover:gap-2.5"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                setTransitionDirection(-1);
                setSelectedHeuristic(null);
              }}
            >
              <ArrowLeft />
              Back
            </Button>
          ) : (
            <h3 className="text-sm font-medium">Prompt quality</h3>
          )}
          <HeuristicScoreBadge
            score={score}
            isLoading={isLoadingHeuristics}
            isDisplay={true}
            isOutdated={isOutdated}
            isError={!!(isError || improvementError)}
            errorMessage={improvementError ?? errorMessage}
          />
        </div>

        {/* Radar chart, heuristic detail, or empty state */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <AnimatePresence
            mode="wait"
            initial={false}
            custom={transitionDirection}
          >
            {selectedHeuristic && selectedScore ? (
              <motion.div
                key="heuristic-feedback"
                className="h-full min-h-0 overflow-y-auto overflow-x-hidden"
                custom={transitionDirection}
                variants={{
                  initial: (dir: 1 | -1) => ({
                    opacity: 0,
                    x: dir > 0 ? 16 : -16,
                  }),
                  animate: { opacity: 1, x: 0 },
                  exit: (dir: 1 | -1) => ({
                    opacity: 0,
                    x: dir > 0 ? -16 : 16,
                  }),
                }}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <HeuristicFeedback
                  heuristicKey={selectedHeuristic}
                  score={selectedScore}
                />
              </motion.div>
            ) : heuristicResults ? (
              <motion.div
                key="radar"
                className="h-full w-full flex flex-col items-center justify-center overflow-x-hidden"
                custom={transitionDirection}
                variants={{
                  initial: (dir: 1 | -1) => ({
                    opacity: 0,
                    x: dir > 0 ? 16 : -16,
                  }),
                  animate: { opacity: 1, x: 0 },
                  exit: (dir: 1 | -1) => ({
                    opacity: 0,
                    x: dir > 0 ? -16 : 16,
                  }),
                }}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <div
                  className={cn(
                    "w-full aspect-square max-h-[80%] transition-[filter] duration-200",
                    (isOutdated || isLoadingHeuristics) &&
                      "grayscale opacity-50 pointer-events-none"
                  )}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart
                      cx="50%"
                      cy="50%"
                      outerRadius="80%"
                      data={radarData}
                      margin={{ top: 20, right: 40, bottom: 20, left: 40 }}
                    >
                      <PolarGrid
                        stroke="currentColor"
                        strokeOpacity={0.2}
                        gridType="polygon"
                      />
                      <PolarAngleAxis
                        dataKey="subject"
                        tick={{
                          fill: "currentColor",
                          fontSize: 12,
                          fontFamily: "var(--font-sans)",
                          color: "var(--text-muted-foreground)",
                          opacity: 1,
                          cursor: "pointer",
                          className: "hover:underline",
                        }}
                        onClick={handleLabelClick}
                      />
                      <PolarRadiusAxis
                        angle={90}
                        domain={[0, 10]}
                        tick={false}
                        axisLine={false}
                      />
                      <Radar
                        name="Score"
                        dataKey="value"
                        stroke={chartColors.stroke}
                        strokeWidth={2}
                        fill={chartColors.fill}
                        fillOpacity={0.15}
                        isAnimationActive={true}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex gap-2 items-center justify-center">
                  {isOutdated && (
                    <TriangleAlert size={12} color="var(--color-yellow-600)" />
                  )}
                  <p className="text-xs text-muted-foreground text-center">
                    {isOutdated
                      ? "This score is outdated."
                      : "Click on a label for more details."}
                  </p>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="empty-state"
                className="h-full flex flex-col items-center justify-center text-center"
                custom={transitionDirection}
                variants={{
                  initial: (dir: 1 | -1) => ({
                    opacity: 0,
                    x: dir > 0 ? 16 : -16,
                  }),
                  animate: { opacity: 1, x: 0 },
                  exit: (dir: 1 | -1) => ({
                    opacity: 0,
                    x: dir > 0 ? -16 : 16,
                  }),
                }}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                {improvementError ? (
                  <NoxusEmptyBlock
                    title="Improvement failed"
                    description={improvementError}
                    icon={TriangleAlert}
                    size="sm"
                    button="Close"
                    onClick={() => setImprovementError(null)}
                  />
                ) : !canAnalyse ? (
                  <NoxusEmptyBlock
                    title="Prompt too short"
                    description="Write at least 200 characters to analyse your prompt quality."
                    icon={Sparkles}
                    size="sm"
                  />
                ) : isError && !isAnalysing ? (
                  <NoxusEmptyBlock
                    title="Analysis failed"
                    description={
                      errorMessage ?? "Something went wrong. Please try again."
                    }
                    icon={TriangleAlert}
                    size="sm"
                    button="Retry analysis"
                    onClick={() => retryEvaluation?.()}
                  />
                ) : isAnalysing ? (
                  <div className="space-y-3">
                    <Loader2
                      size={24}
                      className="animate-spin text-muted-foreground mx-auto"
                    />
                    <p className="text-sm text-muted-foreground">
                      Analysing prompt quality...
                    </p>
                  </div>
                ) : (
                  <NoxusEmptyBlock
                    title="Ready to analyse"
                    description="Evaluate clarity, specificity, structure, and more."
                    icon={Search}
                    size="sm"
                    button="Analyse prompt"
                    onClick={() => triggerEvaluation?.()}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      {/* Bottom actions (fixed) */}
      <div className="flex-shrink-0">
        {heuristicResults && (
          <>
            <div className="p-6">
              <NoxusTooltip
                classNameTrigger="w-full"
                content={refineButtonTooltip}
              >
                <Button
                  onClick={
                    isOutdated
                      ? () => triggerEvaluation?.()
                      : handleRefineFromAnalysis
                  }
                  disabled={isRefineButtonDisabled}
                  variant="outline"
                  className="w-full gap-2"
                >
                  {isImproving || isLoadingHeuristics ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : isOutdated ? (
                    <Search size={16} />
                  ) : (
                    <Sparkles size={16} />
                  )}
                  {isImproving
                    ? "Improving..."
                    : isLoadingHeuristics
                      ? "Analysing..."
                      : isOutdated
                        ? "Run analysis"
                        : "Refine from analysis"}
                </Button>
              </NoxusTooltip>
            </div>
            {/* Or divider */}
            <div className="flex items-center gap-4 text-xs text-ring">
              <Separator className="flex-1" />
              <span className="text-xs text-regular">Or</span>
              <Separator className="flex-1" />
            </div>
          </>
        )}

        {/* Refine with instructions */}
        <div className="space-y-2 p-6 pt-4">
          <h4 className="text-sm font-medium">Refine with instructions</h4>
          <div className="relative">
            <Textarea
              placeholder="Describe how to improve your prompt"
              value={instructionsInput}
              onChange={(e) => setInstructionsInput(e.target.value)}
              className="resize-none min-h-35 text-sm pr-10"
              disabled={isImproving}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleRefineWithInstructions();
                }
              }}
            />
            <Button
              onClick={handleRefineWithInstructions}
              disabled={isImproving || !instructionsInput}
              data-testid="prompt-refine-send"
              size="icon"
              className="absolute bottom-2 right-2"
            >
              {isImproving ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <ArrowUp size={14} />
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
